import { create } from 'zustand'
import { CNSProject } from '@/models/cns'
import { fetchProjectsByWorkspace, createProject, renameProject as serviceRenameProject, deleteProject as deleteProjectService } from '@/services/projectService'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { getErrorMessage } from '@/types/problem-detail'

interface ProjectStore {
  // State
  projects: CNSProject[]
  loading: boolean
  error: string | null
  isInitialized: boolean
  /** Workspace id we last fetched for (so we refetch when workspace changes) */
  _lastFetchedWorkspaceId: string | null
  /** In-flight fetch promise so concurrent callers share one request */
  _fetchPromise: Promise<void> | null
  onProjectDeleted: ((projectId: string) => void) | null
  currentProject: CNSProject | null

  // Actions
  fetchProjects: () => Promise<void>
  setProjects: (projects: CNSProject[]) => void
  addProject: (project: CNSProject) => void
  updateProject: (projectId: string, updates: Partial<CNSProject>) => void
  createProject: (title: string) => Promise<CNSProject>
  renameProject: (projectId: string, newTitle: string) => Promise<CNSProject>
  deleteProject: (projectId: string) => Promise<void>
  setOnProjectDeleted: (callback: ((projectId: string) => void) | null) => void
  setCurrentProject: (project: CNSProject | null) => void
  /** Clears cached projects when the active workspace changes (before refetch). */
  invalidateForWorkspaceSwitch: () => void
  clearAll: () => void
  
  // Getters
  getProjectById: (id: string) => CNSProject | undefined
  getProjectByTitle: (title: string) => CNSProject | undefined
  getCurrentProject: () => CNSProject | undefined
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  // Initial state
  projects: [],
  loading: false,
  error: null,
  isInitialized: false,
  _lastFetchedWorkspaceId: null,
  _fetchPromise: null,
  onProjectDeleted: null,
  currentProject: null,

  // Fetch projects for the current workspace only (workspace-aware; no fallback to GET /projects).
  // Refetches when workspace changes (call fetchProjects after setCurrentWorkspace in workspace switcher).
  fetchProjects: async () => {
    const state = get()
    const currentWorkspaceId = useWorkspaceStore.getState().currentWorkspaceId
    const sameContext = currentWorkspaceId
      ? state._lastFetchedWorkspaceId === currentWorkspaceId && state.isInitialized
      : state._lastFetchedWorkspaceId === null && state.isInitialized
    if (sameContext) return
    if (state._fetchPromise) {
      await state._fetchPromise
      return
    }
    const promise = (async () => {
      try {
        const projectsData = await fetchProjectsByWorkspace(currentWorkspaceId)
        set((s) => ({
          projects: projectsData,
          loading: false,
          error: null,
          isInitialized: true,
          _lastFetchedWorkspaceId: currentWorkspaceId,
          _fetchPromise: null,
          currentProject: s.currentProject || (projectsData.length > 0 ? projectsData[0] : null),
        }))
      } catch (error) {
        set({ error: getErrorMessage(error, 'Failed to fetch projects'), loading: false, _fetchPromise: null })
      }
    })()
    set({ loading: true, error: null, _fetchPromise: promise })
    await promise
  },

  // Set projects (e.g. after direct API call in resolve flow when store was empty)
  setProjects: (projects) => {
    set({ projects, isInitialized: true, loading: false, error: null })
  },

  // Add a project to the store
  addProject: (project) => {
    set((state) => ({ 
      projects: [...state.projects, project] 
    }))
  },
  
  // Update a project in the store
  updateProject: (projectId, updates) => {
    set((state) => ({
      projects: state.projects.map(project => 
        project.id === projectId 
          ? { ...project, ...updates }
          : project
      )
    }))
  },
  
  // Create a new project in the current workspace (requires a workspace to be selected)
  createProject: async (title: string) => {
    set({ loading: true, error: null })
    const currentWorkspaceId = useWorkspaceStore.getState().currentWorkspaceId
    if (!currentWorkspaceId) {
      set({ loading: false, error: "Select a workspace before creating a project" })
      throw new Error("Select a workspace before creating a project")
    }
    try {
      const newProject = await createProject(title, currentWorkspaceId)
      set((state) => ({ 
        projects: [...state.projects, newProject],
        loading: false,
        currentProject: newProject // Set the new project as current
      }))
      return newProject
    } catch (error) {
      set({ 
        error: getErrorMessage(error, 'Failed to create project'), 
        loading: false 
      })
      throw error
    }
  },
  
  // Rename a project
  renameProject: async (projectId: string, newTitle: string) => {
    set({ loading: true, error: null })
    
    try {
      const updatedProject = await serviceRenameProject(projectId, newTitle)
      set((state) => ({
        projects: state.projects.map(project => 
          project.id === projectId 
            ? { ...project, title: updatedProject.title }
            : project
        ),
        loading: false
      }))
      return updatedProject
    } catch (error) {
      set({ 
        error: getErrorMessage(error, 'Failed to rename project'), 
        loading: false 
      })
      throw error
    }
  },
  
  // Delete a project
  deleteProject: async (projectId: string) => {
    set({ loading: true, error: null })
    
    try {
      await deleteProjectService(projectId)
      set((state) => {
        const remainingProjects = state.projects.filter(project => project.id !== projectId)
        // If we deleted the current project, set the first remaining project as current
        const newCurrentProject = state.currentProject?.id === projectId 
          ? (remainingProjects.length > 0 ? remainingProjects[0] : null)
          : state.currentProject
          
        return {
          projects: remainingProjects,
          currentProject: newCurrentProject
        }
      })
      set({ loading: false })
      get().onProjectDeleted?.(projectId)
    } catch (error) {
      set({ 
        error: getErrorMessage(error, 'Failed to delete project'), 
        loading: false 
      })
      throw error
    }
  },
  
  // Set the callback for project deletion
  setOnProjectDeleted: (callback: ((projectId: string) => void) | null) => {
    set({ onProjectDeleted: callback })
  },
  
  // Set the current active project
  setCurrentProject: (project: CNSProject | null) => {
    set({ currentProject: project })
  },

  invalidateForWorkspaceSwitch: () => {
    set({
      projects: [],
      isInitialized: false,
      _lastFetchedWorkspaceId: null,
      currentProject: null,
      error: null,
    })
  },
  
  // Clear all project data
  clearAll: () => {
    set({
      projects: [],
      loading: false,
      error: null,
      isInitialized: false,
      _lastFetchedWorkspaceId: null,
      _fetchPromise: null,
      onProjectDeleted: null,
      currentProject: null,
    })
  },
  
  // Get a project by ID
  getProjectById: (id: string) => {
    return get().projects.find(project => project.id === id)
  },
  
  // Get a project by title
  getProjectByTitle: (title: string) => {
    return get().projects.find(project => project.title === title)
  },
  
  // Get the current active project
  getCurrentProject: () => {
    return get().currentProject || undefined
  }
}))
